/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {ResourceHelper} from '../../../util/resource-helper';
import {ApiConfig} from '../../api';
import {AdnHttpService} from '../../../util/adn-http.service';
import {
  createAdnHttpResource,
  createAdnHttpResourceAction,
  createAdnHttpResourceSender
} from '../../../util/create-adn-http-resource';
import {LocalUserProfile} from '../../../session/local-user-profile';
import {DataViewResource} from './data-view';
import {AuthHttpInterceptorService} from '../../../auth/auth-http-interceptor.service';
import {ApiErrorHandlerService} from '../../../../app/common/directives/error/api-error-handler.service';

/** Angular DI token for the Order HTTP resource (factory product). */
export class OrderResource {
}

export function updateEndDateForManualInput(obj: any, param: string): void {
  if (obj[param] && obj[param] instanceof Date && obj[param].getMinutes() === 59 && (obj[param].getSeconds() < 59 || obj[param].getMilliseconds() < 999)) {
    obj[param].setSeconds(59, 999);
  }
}

export function createOrderResource(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: AuthHttpInterceptorService,
  ApiErrorHandler: ApiErrorHandlerService,
  localUserProfile: LocalUserProfile,
  dataViewResource: any
): any {
  const transformRequest = function(order: any) {
    updateEndDateForManualInput(order, 'objectiveEndDate');
    ResourceHelper.checkObjectives(order);
    delete order.reportExecutions;
    return order;
  };
  const send = createAdnHttpResourceSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  const Order = createAdnHttpResource(
    send,
    'orders',
    () => localUserProfile,
    undefined,
    {showBudgetSpend: true},
    {transformRequest}
  );
  Order.copy = createAdnHttpResourceAction(send, {
    method: 'POST',
    url: ApiConfig.obtain().getVersionedUri('orders') + '/:id/copy',
    defaultParams: {showBudgetSpend: true},
    queryParams: [
      'showBudgetSpend',
      'orderId',
      'advertiserId',
      'name',
      'copyLineItems',
      'copyTargeting',
      'copyCreatives',
      'copyReportSchedules',
      'clearAdvertiser',
      'clearBudgets',
      'lineItemUserState'
    ]
  });

  Order.getListView = function(queryParams: any) {
    return dataViewResource.getView(queryParams, 'OrderListView').then(function(page: any) {
      _.forEach(page.results, function(datum: any) {
        _.set(datum, 'order', new Order(datum.order));
      });
      return page;
    });
  };

  Order.copyOrder = function(order: any, newId: any, lineItemUserState: any, copyLineItems: any, copyTargeting: any, copyCreatives: any, copyBudgets: any, copyReportSchedules: any) {
    if (!order || !order.id) {
      return Promise.reject('Insufficient parameters for the order copy');
    }
    const advId = _.get(order, ['advertiser', 'id'], '');
    return Order.copy({
      id: order.id,
      orderId: newId || '',
      name: order.name,
      advertiserId: advId || '',
      copyTargeting: _.isBoolean(copyTargeting) ? copyTargeting : true,
      copyCreatives: _.isBoolean(copyCreatives) ? copyCreatives : true,
      copyLineItems: _.isBoolean(copyLineItems) ? copyLineItems : true,
      copyReportSchedules: _.isBoolean(copyReportSchedules) ? copyReportSchedules : true,
      clearAdvertiser: true,
      clearBudgets: _.isBoolean(copyBudgets) ? !copyBudgets : false,
      lineItemUserState: lineItemUserState || ''
    });
  };

  Order.getDataView = function(params: any) {
    return dataViewResource.get('OrderView', params).then(function(dataView: any) {
      const order = new Order(dataView.order);
      order.advertiser = dataView.advertiser;
      return order;
    });
  };

  return Order;
}

export const orderResourceProvider = {
  provide: OrderResource,
  useFactory: createOrderResource,
  deps: [AdnHttpService, AuthHttpInterceptorService, ApiErrorHandlerService, LocalUserProfile, DataViewResource]
};

